import type en from '../en/auth'
import type { Translation } from '../types'

/** Signing in, passwords, two-factor sign-in and the sessions of an account */
const auth: Translation<typeof en> = {
  "auth.tagline": "Platform voor netwerkbeheer",
  "auth.ssoCouldNotStart": "Single sign-on kon niet starten",
  "auth.loginFailed": "Inloggen mislukt",
  "auth.idleSignedOut": "U bent uitgelogd na een periode zonder activiteit. Log opnieuw in om verder te gaan.",
  "auth.emailPlaceholder": "u@bedrijf.nl",
  "auth.signIn": "Inloggen",
  "auth.signInSso": "Inloggen met single sign-on",
  "auth.ssoByEmailHint": "Uw werkadres bepaalt bij welk bedrijf u inlogt.",
  "auth.continueSso": "Doorgaan met single sign-on",
  "auth.forgotPassword": "Wachtwoord vergeten?",
  "auth.resetFailed": "Kon de herstelmail niet versturen",
  "auth.setFailed": "Kon het wachtwoord niet instellen",
  "auth.resetTitle": "Wachtwoord herstellen",
  "auth.setTitle": "Stel uw wachtwoord in",
  "auth.resetSubtitle": "Vul uw e-mailadres in om een herstellink te ontvangen",
  "auth.setSubtitle": "Maak een veilig wachtwoord voor uw account",
  "auth.checkEmail": "Controleer uw e-mail",
  "auth.passwordSet": "Wachtwoord ingesteld",
  "auth.resetSent": "Als dat e-mailadres bestaat, is er een herstellink verstuurd.",
  "auth.accountActive": "Uw account is nu actief. U kunt inloggen.",
  "auth.goToLogin": "Naar inloggen",
  "auth.sendResetLink": "Herstellink versturen",
  "auth.backToLogin": "Terug naar inloggen",
  "auth.newPassword": "Nieuw wachtwoord",
  "auth.confirmPassword": "Bevestig wachtwoord",
  "auth.check.length": "Minstens 12 tekens",
  "auth.check.upper": "Hoofdletter",
  "auth.check.lower": "Kleine letter",
  "auth.check.number": "Cijfer",
  "auth.check.match": "Wachtwoorden komen overeen",
  "auth.setAndActivate": "Wachtwoord instellen en account activeren",
  "auth.landing.title": "Waar wilt u naartoe?",
  "auth.landing.signedInAs": "Ingelogd als {email}",
  "auth.landing.noAccess": "U hebt nog geen toegang tot een bedrijf. Vraag een beheerder om u toe te voegen.",
  "auth.landing.platform": "Platformbeheer",
  "auth.landing.platformSub": "Bedrijven, licenties, edge-proxy en instellingen",
  "auth.landing.companyAdmin": "Bedrijfsbeheerder",
  "auth.landing.member": "Lid",
  "auth.ssoIncomplete": "Het inloggen kon niet worden voltooid. Log opnieuw in.",
  "auth.backToSignIn": "Terug naar inloggen",
  "auth.signingIn": "U wordt ingelogd…",
  "auth.reauth.title": "Bevestig dat u het bent",
  "auth.reauth.sso": "Voor deze wijziging moet u opnieuw inloggen bij uw identiteitsprovider (hooguit eens per 10 minuten). Voer de wijziging daarna nog een keer uit.",
  "auth.reauth.idpUnreachable": "Kon de identiteitsprovider niet bereiken",
  "auth.reauth.password": "Voor deze wijziging is uw wachtwoord opnieuw nodig. De komende 10 minuten wordt er niet opnieuw om gevraagd.",
  "auth.tf.startAgain": "← Opnieuw beginnen",
  "auth.tf.setupTitle": "Inloggen in twee stappen instellen",
  "auth.tf.setupIntro": "Uw account heeft bij elke keer inloggen een code uit een authenticator-app nodig. Stel dit nu eenmalig in.",
  "auth.tf.title": "Inloggen in twee stappen",
  "auth.tf.enterRecovery": "Vul een van uw herstelcodes in. Elke code werkt één keer.",
  "auth.tf.enterCode": "Vul de 6-cijferige code in die uw authenticator-app nu toont.",
  "auth.tf.recoveryCode": "Herstelcode",
  "auth.tf.code": "Code",
  "auth.tf.verify": "Verifiëren",
  "auth.tf.useApp": "Een code uit de app gebruiken",
  "auth.tf.lostPhone": "Telefoon kwijt? Gebruik een herstelcode",
  "auth.tf.couldNotStart": "Kon het instellen niet starten",
  "auth.tf.savedContinue": "Ik heb ze bewaard — doorgaan",
  "auth.tf.saved": "Ik heb ze bewaard",
  "auth.tf.step1": "Open een authenticator-app op uw telefoon (Google Authenticator, Microsoft Authenticator, 1Password, …).",
  "auth.tf.step2": "Scan de QR-code of typ de sleutel over.",
  "auth.tf.step3": "Vul de 6-cijferige code in die de app toont.",
  "auth.tf.qrAlt": "QR-code met de sleutel, om te scannen met uw authenticator-app",
  "auth.tf.key": "Sleutel",
  "auth.tf.codeFromApp": "Code uit de app",
  "auth.tf.turnOn": "Inschakelen",
  "auth.tf.saveCodes": "Bewaar deze herstelcodes op een veilige plek, zoals een wachtwoordmanager. Met elke code kunt u één keer inloggen als u uw telefoon kwijt bent. Ze worden alleen nu getoond.",
  "auth.tf.recoveryCodes": "Herstelcodes",
  "auth.tf.codesCopied": "Herstelcodes gekopieerd",
  "auth.tf.copyHint": "Selecteer de codes en kopieer ze, of download ze.",
  "auth.prot.ssoLinked": "Single sign-on gekoppeld",
  "auth.prot.ssoLinkedText": "U kunt nu inloggen met uw identiteitsprovider.",
  "auth.prot.couldNotStart": "Kon niet starten",
  "auth.prot.title": "Inlogbeveiliging",
  "auth.prot.idleLabel": "Mij uitloggen na een periode zonder activiteit",
  "auth.prot.afterMax": "Na {duration} (het langste dat hier is toegestaan)",
  "auth.prot.after": "Na {duration}",
  "auth.prot.never": "Nooit",
  "auth.prot.staySignedIn": "U blijft ingelogd terwijl u het dashboard niet gebruikt, totdat de sessie haar maximale duur bereikt.",
  "auth.prot.signedOutAfter": "Een sessie die u open laat, wordt {duration} na uw laatste gebruik uitgelogd.",
  "auth.prot.newDeviceMail": "Mail mij wanneer mijn account inlogt vanaf een apparaat dat het nog niet eerder heeft gebruikt",
  "auth.prot.sso": "Single sign-on",
  "auth.prot.linked": "Gekoppeld",
  "auth.prot.linkedText": "U kunt inloggen met de identiteitsprovider van uw bedrijf.",
  "auth.prot.linkText": "Uw bedrijf logt in via zijn identiteitsprovider. Koppel uw account om die te gebruiken.",
  "auth.prot.linkMine": "Mijn account koppelen",
  "auth.sec.isOff": "Inloggen in twee stappen is uitgeschakeld",
  "auth.sec.isOn": "Inloggen in twee stappen is ingeschakeld",
  "auth.sec.signedOutDevice": "Uitgelogd op dat apparaat",
  "auth.sec.signedOutOthers": { one: "Uitgelogd op {count} ander apparaat", other: "Uitgelogd op {count} andere apparaten" },
  "auth.sec.couldNotSignOut": "Kon niet uitloggen",
  "auth.sec.newSetNote": "Een nieuwe set vervangt de oude: de oude codes werken dan niet meer.",
  "auth.sec.offNote": "Voor inloggen is dan alleen uw wachtwoord weer nodig.",
  "auth.sec.codeFromYourApp": "Code uit uw authenticator-app",
  "auth.sec.newCodes": "Nieuwe herstelcodes",
  "auth.sec.turnOff": "Uitschakelen",
  "auth.sec.on": "Aan",
  "auth.sec.since": { one: "sinds {date} · nog {count} herstelcode", other: "sinds {date} · nog {count} herstelcodes" },
  "auth.sec.required": " · verplicht voor uw account",
  "auth.sec.offText": "Uit. Met inloggen in twee stappen is een gestolen wachtwoord niet genoeg: voor inloggen is ook een code uit een app op uw telefoon nodig.",
  "auth.sec.whereSignedIn": "Waar u bent ingelogd",
  "auth.sec.thisDevice": "Dit apparaat",
  "auth.sec.signedInWhen": "ingelogd {when}",
  "auth.sec.activeWhen": "actief {when}",
  "auth.sec.endsDate": "eindigt {date}",
  "auth.sec.signOutDevice": "{device} uitloggen, ingelogd {when}",
  "auth.sec.confirmSignOutAll": "Uitloggen op alle andere apparaten?",
  "auth.sec.signOutOthers": "Andere apparaten uitloggen",
  "auth.landing.client": "Evenementen van {customer}",
  "auth.landing.clientOpens": "Evenementen van {customer} — open vanaf {when}",
  "auth.landing.clientNoEvent": "Evenementen van {customer} — open bij het volgende evenement",
  "auth.clientWindowClosed": "Inloggen kan alleen tijdens uw evenement",
  "auth.app.stayPrompt": "Aangemeld blijven op deze telefoon",
  "auth.app.unlockReason": "Velanox ontgrendelen",
  "auth.app.unlock": "Ontgrendelen",
  "auth.app.changeServer": "Andere server",
  "auth.app.offline": "U bent offline",
}

export default auth
